
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

// Helper to detect browser environment restrictions
export function detectBrowserRestrictions(): {
  iFrameContext: boolean;
  sandboxed: boolean;
  securityPolicy: boolean;
  downloadBlocked: boolean;
} {
  try {
    const iFrameContext = window.self !== window.top;
    const sandboxed = window.origin === "null";
    
    // Check for restrictive Content-Security-Policy
    let securityPolicy = false;
    let downloadBlocked = false;
    
    try {
      // Attempt to create a blob URL - this will fail in some restrictive environments
      const testBlob = new Blob(['test']);
      const testUrl = URL.createObjectURL(testBlob);
      URL.revokeObjectURL(testUrl);
    } catch (e) {
      console.warn("Blob URL creation failed - security restrictions detected:", e);
      securityPolicy = true;
      downloadBlocked = true;
    }
    
    // Check if the download attribute is supported
    if (!('download' in document.createElement('a'))) {
      console.warn("Download attribute not supported in this browser");
      downloadBlocked = true;
    }
    
    const result = { iFrameContext, sandboxed, securityPolicy, downloadBlocked };
    console.log("Browser environment detection:", result);
    return result;
  } catch (e) {
    // If we can't access window.top due to cross-origin restrictions
    console.warn("Cross-origin restrictions detected:", e);
    return { iFrameContext: true, sandboxed: true, securityPolicy: true, downloadBlocked: true };
  }
}

// Calculate sativa percentage from indica percentage
export function calculateSativaPercentage(indicaPercentage: number): number {
  return Math.max(0, Math.min(100, 100 - indicaPercentage));
}

/**
 * Creates a debounced function that delays invoking the provided function
 * until after 'wait' milliseconds have elapsed since the last time it was invoked.
 * @param func The function to debounce
 * @param wait The number of milliseconds to delay
 * @returns A debounced version of the provided function
 */
export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(...args: Parameters<T>): void {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(later, wait);
  };
}

/**
 * Calculates the total cannabinoid percentage by combining a neutral form with its acidic precursor.
 * Uses a standard conversion factor of 0.877 for decarboxylation of acidic forms.
 * 
 * @param neutralValue - The percentage of the neutral cannabinoid form (e.g., THC, CBD)
 * @param acidValue - The percentage of the acidic cannabinoid form (e.g., THCA, CBDA)
 * @returns The total cannabinoid percentage, or null if both inputs are null/invalid
 */
export function calculateTotalCannabinoid(neutralValue: number | null | undefined, acidValue: number | null | undefined): number | null {
  // Convert undefined to null for consistent handling
  const neutral = neutralValue === undefined ? null : neutralValue;
  const acid = acidValue === undefined ? null : acidValue;
  
  // Handle cases where both values are null or non-numeric
  if ((neutral === null || isNaN(Number(neutral))) && 
      (acid === null || isNaN(Number(acid)))) {
    return null;
  }
  
  // Convert inputs to numbers, defaulting to 0 if null or non-numeric
  const neutralNum = (neutral === null || isNaN(Number(neutral))) ? 0 : Number(neutral);
  const acidNum = (acid === null || isNaN(Number(acid))) ? 0 : Number(acid);
  
  // Apply the conversion factor (0.877) to the acidic form and add to the neutral form
  const total = neutralNum + (acidNum * 0.877);
  
  return total;
}
